import React, { useEffect, useState, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import Breadcrumbs from "@/components/Breadcrumbs";
import CTA from "@/components/CTA";
import Seo, { faqSchema, articleSchema } from "@/components/Seo";
import { CaretRight } from "@phosphor-icons/react";

export function FAQPage() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("All");
  useEffect(() => { api.get("/faqs").then((r) => setItems(r.data)); }, []);
  const cats = useMemo(() => ["All", ...Array.from(new Set(items.map(f => f.category)))], [items]);
  const filtered = items.filter(f => (cat === "All" || f.category === cat) && (q === "" || (f.question + f.answer).toLowerCase().includes(q.toLowerCase())));
  return (
    <div data-testid="faq-page">
      <Seo
        title="Frequently Asked Questions"
        description="Answers about solar surveillance trailers, deployment timing, monitoring, video storage, cellular connectivity, and service coverage in Nevada and Utah."
        path="/faq"
        schema={faqSchema(items)}
      />
      <section className="container-x pt-10 pb-6"><Breadcrumbs items={[{ label: "FAQ" }]} /></section>
      <section className="container-x pb-10">
        <div className="eyebrow text-solar mb-3">Frequently asked</div>
        <h1 className="mb-4">Answers before you ask.</h1>
        <p className="text-white/75 text-lg max-w-2xl">Answers about deployment, systems, monitoring, power, storage and service areas.</p>
      </section>
      <section className="container-x pb-20 grid lg:grid-cols-[240px_1fr] gap-10">
        <aside className="lg:sticky lg:top-28 self-start">
          <input data-testid="faq-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search FAQs" className="w-full bg-steel border border-chrome px-4 py-3 text-sm text-white focus:border-solar outline-none mb-4" />
          <div className="border border-chrome bg-steel">
            {cats.map((c) => (
              <button key={c} data-testid={`faq-cat-${c}`} onClick={() => setCat(c)} className={`w-full text-left px-4 py-3 text-sm uppercase tracking-wider font-semibold border-b border-chrome last:border-b-0 ${cat === c ? "bg-obsidian text-solar" : "text-white/75 hover:text-solar"}`}>
                {c}
              </button>
            ))}
          </div>
        </aside>
        <div className="divide-y divide-chrome border-t border-b border-chrome">
          {filtered.map((f) => (
            <details key={f.slug} className="group py-5" data-testid={`faq-item-${f.slug}`}>
              <summary className="cursor-pointer list-none flex items-start justify-between gap-6">
                <span className="font-semibold text-white text-lg">{f.question}</span>
                <CaretRight size={18} weight="bold" className="text-solar mt-1 shrink-0 group-open:rotate-90 transition-transform" />
              </summary>
              <div className="mt-3 text-white/75 leading-relaxed">{f.answer}</div>
            </details>
          ))}
          {filtered.length === 0 && (<div className="py-10 text-white/60">No matching FAQs.</div>)}
        </div>
      </section>
      <CTA />
    </div>
  );
}

export function ResourcesIndex() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/articles").then((r) => setItems(r.data)); }, []);
  return (
    <div data-testid="resources-index">
      <Seo title="Learning Center — Solar Surveillance Guides" description="Educational articles on solar surveillance trailers, construction site security, remote video monitoring and rapid-deployment surveillance for commercial sites." path="/resources" />
      <section className="container-x pt-10 pb-6"><Breadcrumbs items={[{ label: "Learning Center" }]} /></section>
      <section className="container-x pb-10">
        <div className="eyebrow text-solar mb-3">Learning Center</div>
        <h1 className="mb-4">Guides on solar surveillance and site security.</h1>
        <p className="text-white/75 text-lg max-w-2xl">Practical guidance for construction managers, property owners and procurement teams.</p>
      </section>
      <section className="container-x pb-20 grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-chrome border border-chrome">
        {items.map((a) => (
          <Link key={a.slug} to={`/resources/${a.slug}`} data-testid={`article-card-${a.slug}`} className="bg-obsidian p-6 hover:bg-steel transition-colors group flex flex-col">
            <div className="eyebrow text-solar mb-3">{a.category} · {a.reading_time}</div>
            <div className="font-display uppercase text-xl font-bold mb-3 group-hover:text-solar transition-colors leading-tight">{a.title}</div>
            <div className="text-white/70 text-sm mb-4 leading-relaxed flex-grow">{a.excerpt}</div>
            <span className="text-solar text-sm font-bold uppercase tracking-wider flex items-center gap-1">Read <CaretRight size={12} weight="bold" /></span>
          </Link>
        ))}
      </section>
      <CTA />
    </div>
  );
}

export function ArticleDetail() {
  const { slug } = useParams();
  const [a, setA] = useState(null);
  useEffect(() => { api.get(`/articles/${slug}`).then((r) => setA(r.data)).catch(() => setA(false)); }, [slug]);
  if (a === false) return <div className="container-x py-20"><h1>Article not found</h1></div>;
  if (!a) return <div className="container-x py-20 text-white/60">Loading…</div>;
  return (
    <div data-testid={`article-detail-${slug}`}>
      <Seo
        title={a.title}
        description={a.excerpt}
        path={`/resources/${slug}`}
        schema={articleSchema(a)}
        breadcrumb={[{ label: "Home", path: "/" }, { label: "Learning Center", path: "/resources" }, { label: a.title }]}
      />
      <section className="container-x pt-10 pb-6"><Breadcrumbs items={[{ label: "Learning Center", to: "/resources" }, { label: a.title }]} /></section>
      <article className="container-x pb-16 max-w-3xl">
        <div className="eyebrow text-solar mb-3">{a.category} · {a.reading_time}</div>
        <h1 className="mb-6">{a.title}</h1>
        <div className="text-white/60 text-sm mb-8">By {a.author} · Published {a.published_date}</div>
        <div className="prose prose-invert max-w-none text-white/85 leading-relaxed text-lg whitespace-pre-line">
          {a.content}
        </div>
      </article>
      <CTA />
    </div>
  );
}

export function CaseStudiesIndex() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/case_studies").then((r) => setItems(r.data)); }, []);
  return (
    <div data-testid="case-studies-index">
      <Seo title="Case Studies — Real Deployments" description="Real 360 View LLC deployments across Nevada — construction site security, equipment yard protection and commercial property surveillance." path="/case-studies" />
      <section className="container-x pt-10 pb-6"><Breadcrumbs items={[{ label: "Case Studies" }]} /></section>
      <section className="container-x pb-10">
        <div className="eyebrow text-solar mb-3">Case Studies</div>
        <h1 className="mb-4">Recent projects.</h1>
        <p className="text-white/75 text-lg max-w-2xl">Real deployments across Nevada. Additional case studies added regularly.</p>
      </section>
      <section className="container-x pb-20 grid md:grid-cols-2 gap-6">
        {items.map((c) => (
          <Link key={c.slug} to={`/case-studies/${c.slug}`} data-testid={`case-card-${c.slug}`} className="border border-chrome bg-obsidian hover:border-solar transition-colors group block">
            <div className="aspect-[16/9] overflow-hidden bg-steel">
              <img src={c.image} alt={c.title} className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity" />
            </div>
            <div className="p-6">
              <div className="eyebrow text-solar mb-2">{c.city}, {c.state} · {c.system_name}</div>
              <div className="font-display uppercase text-2xl font-bold mb-3 group-hover:text-solar transition-colors">{c.title}</div>
              <div className="text-white/70 text-sm leading-relaxed">{c.problem}</div>
            </div>
          </Link>
        ))}
      </section>
      <CTA />
    </div>
  );
}

export function CaseStudyDetail() {
  const { slug } = useParams();
  const [c, setC] = useState(null);
  useEffect(() => { api.get(`/case_studies/${slug}`).then((r) => setC(r.data)).catch(() => setC(false)); }, [slug]);
  if (c === false) return <div className="container-x py-20"><h1>Case study not found</h1></div>;
  if (!c) return <div className="container-x py-20 text-white/60">Loading…</div>;
  return (
    <div data-testid={`case-detail-${slug}`}>
      <Seo
        title={c.title}
        description={c.problem}
        path={`/case-studies/${slug}`}
        ogImage={c.image}
        breadcrumb={[{ label: "Home", path: "/" }, { label: "Case Studies", path: "/case-studies" }, { label: c.title }]}
      />
      <section className="container-x pt-10 pb-6"><Breadcrumbs items={[{ label: "Case Studies", to: "/case-studies" }, { label: c.title }]} /></section>
      <section className="container-x pb-10">
        <div className="eyebrow text-solar mb-3">{c.city}, {c.state} · {c.industry_slug}</div>
        <h1 className="mb-5">{c.title}</h1>
      </section>
      <section className="container-x pb-12">
        <div className="aspect-[16/9] overflow-hidden border border-chrome mb-10">
          <img src={c.image} alt={c.title} className="w-full h-full object-cover" />
        </div>
        <div className="grid md:grid-cols-3 gap-px bg-chrome border border-chrome mb-10">
          <InfoBlock title="Site Type" body={c.site_type} />
          <InfoBlock title="System Deployed" body={c.system_name} />
          <InfoBlock title="Duration" body={c.duration} />
        </div>
        <div className="grid lg:grid-cols-2 gap-10">
          <div>
            <div className="eyebrow text-solar mb-3">The Problem</div>
            <p className="text-white/85 leading-relaxed text-lg">{c.problem}</p>
          </div>
          <div>
            <div className="eyebrow text-solar mb-3">Outcome</div>
            <p className="text-white/85 leading-relaxed text-lg">{c.outcome}</p>
            <div className="mt-6 eyebrow text-solar">Monitoring</div>
            <p className="text-white/75 mt-2">{c.monitoring}</p>
          </div>
        </div>
      </section>
      <CTA />
    </div>
  );
}

function InfoBlock({ title, body }) {
  return (
    <div className="bg-obsidian p-6">
      <div className="eyebrow text-solar mb-2">{title}</div>
      <div className="font-semibold text-white">{body}</div>
    </div>
  );
}
